import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../utils/api.js';
import { compressImage } from '../utils/image.js';
import { readLocalJson, KEYS } from '../utils/storage.js';
import { recentApiErrors } from '../utils/recentErrors.js';
import { getFeedbackContext } from '../utils/feedbackContext.js';
import { friendlyError } from '../utils/errors.js';
import { REPORT_CATEGORIES, MAX_MESSAGE_CHARS } from '../../shared/feedback.js';

// The Feedback button's sheet (D-LAUNCH3). The draft lives in this component's
// state, which survives closing the sheet and a failed send — a tech's words
// are never silently lost.
export default function FeedbackSheet({ open, onClose }) {
  const location = useLocation();
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState('');
  const [photo, setPhoto] = useState(null); // compressed JPEG data URL
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Separate from `error`: drives the button's "Try again" label. Only a
  // failed SEND sets this — a validation nudge ("pick a category") or a
  // photo-read failure sets `error` but must not make a clean retry look
  // like a repeat failure.
  const [sendFailed, setSendFailed] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);
  const titleRef = useRef(null);

  // The sheet stays mounted (this component always renders; `open` just
  // toggles the early return below), so a send started before the tech
  // closes it keeps running and can still land — see handleClose for what
  // that means for `sent`.
  useEffect(() => {
    if (open) {
      // A failed send's reason should still be there when the tech reopens
      // the sheet to retry — only a clean open (no prior failure) starts
      // with a blank slate.
      if (!sendFailed) setError(null);
      // Accessible open: move focus onto the sheet instead of leaving it on
      // whatever the tech tapped underneath. `sent` is also a dependency so
      // focus follows the heading when the thanks screen replaces the form
      // (a send that completes while the sheet is still open), not just on
      // open/close.
      titleRef.current?.focus();
    }
  }, [open, sent]);

  if (!open) return null;

  // Every way of leaving the sheet (✕, backdrop, Escape, Done) goes through
  // here. `sent` is deliberately NOT reset by the open-effect above: a send
  // that completes after the tech already closed the sheet still needs to
  // show the thanks screen the next time they open it. So `sent` is only
  // cleared here, on the close that follows it having been shown — which
  // covers both "tapped Done" and "dismissed the thanks screen another way".
  function handleClose() {
    if (sent) setSent(false);
    onClose();
  }

  async function pickPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setPhoto(await compressImage(file, { maxDimension: 1600, quality: 0.8 }));
      setError(null);
      setSendFailed(false);
    } catch {
      setError('Could not read that photo. Try another one.');
    }
  }

  async function send() {
    if (!category || busy) return;
    if (!message.trim() && !photo) {
      setError('Say what happened, or attach a photo.');
      return;
    }
    setBusy(true);
    setError(null);
    setSendFailed(false);
    try {
      // The scan only describes what's on screen on the Result and
      // Troubleshoot pages; elsewhere it may be stale, so don't attach it.
      // Trailing slashes are normalized first so '/result/' still counts.
      const path = location.pathname.replace(/\/+$/, '') || '/';
      const onJob = path === '/result' || path === '/troubleshoot';
      const scan = onJob ? readLocalJson(KEYS.lastScan, null)?.result || null : null;
      const payload = {
        category,
        message: message.trim(),
        route: location.pathname + location.search,
        model_number: scan?.model_number || null,
        lookup_id: scan?.lookup_id || null,
        session_id: onJob ? getFeedbackContext().sessionId : null,
        recent_errors: recentApiErrors(),
      };
      const photoBlob = photo ? await (await fetch(photo)).blob() : null;
      await api.sendFeedback({ payload, photoBlob });
      setCategory(null);
      setMessage('');
      setPhoto(null);
      setSent(true);
    } catch (e) {
      setError(friendlyError(e, 'send your feedback'));
      setSendFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-start sm:pt-10 justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
      onClick={(e) => {
        // Closable even mid-send: the sheet stays mounted, so the request
        // finishes in the background instead of being stuck behind a
        // backdrop the tech can't dismiss.
        if (e.target === e.currentTarget) handleClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleClose();
      }}
    >
      <div className="w-full sm:max-w-lg bg-ink-800 border border-ink-700 rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto overscroll-contain">
        {sent ? (
          <div className="text-center space-y-4 py-4">
            <div className="text-5xl" aria-hidden="true">🙏</div>
            <h2 id="feedback-title" tabIndex={-1} ref={titleRef} className="text-xl font-bold text-ink-50 outline-none">Thanks — Jason's team will see this.</h2>
            <p className="text-ink-300">Keep them coming. Every report gets read.</p>
            <button type="button" onClick={handleClose} className="btn-primary w-full">Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <h2 id="feedback-title" tabIndex={-1} ref={titleRef} className="text-xl font-bold text-ink-50 outline-none">Send feedback</h2>
              <div className="flex items-center gap-1 shrink-0">
                {/* iPad landscape: the on-screen keyboard can cover the big
                    bottom Send button entirely, so a compact one lives up
                    here too — same guard and handler, just smaller. */}
                <button
                  type="button"
                  onClick={send}
                  disabled={!category || busy}
                  className="inline-flex items-center min-h-[44px] px-4 rounded-lg bg-mra-red text-ink-950 text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? 'Sending…' : 'Send'}
                </button>
                <button type="button" onClick={handleClose} className="btn-ghost px-3" aria-label="Close">✕</button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {REPORT_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={category === c.id}
                  disabled={busy}
                  onClick={() => {
                    setCategory(c.id);
                    setError(null);
                    setSendFailed(false);
                  }}
                  className={`min-h-[56px] rounded-xl border px-3 font-semibold disabled:opacity-50 ${
                    category === c.id ? 'bg-mra-red/20 border-mra-red text-ink-50' : 'bg-ink-900 border-ink-700 text-ink-200'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {message.trim() && !category && (
              <p className="text-ink-400 text-sm">Pick one above to send.</p>
            )}

            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setError(null);
                setSendFailed(false);
              }}
              rows={4}
              maxLength={MAX_MESSAGE_CHARS}
              readOnly={busy}
              placeholder="What happened? Tap the mic on the keyboard to dictate."
              className="w-full px-4 py-3 bg-ink-900 border border-ink-700 rounded-xl text-ink-50 text-base focus:border-mra-red outline-none read-only:opacity-70"
            />

            <div className="flex items-center gap-3">
              {photo ? (
                <>
                  <img src={photo} alt="Attached" className="h-16 w-16 object-cover rounded-lg border border-ink-700" />
                  <button type="button" onClick={() => setPhoto(null)} disabled={busy} className="btn-ghost disabled:opacity-50">Remove photo</button>
                </>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary flex-1 disabled:opacity-50">
                  📎 Attach a screenshot or photo
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} disabled={busy} />
            </div>

            <p className="text-ink-400 text-sm">We'll attach which screen you're on and the model, so you don't have to type them.</p>
            {error && <p role="alert" className="text-fail-400 text-sm">{error}</p>}

            <button type="button" onClick={send} disabled={!category || busy} className="btn-primary w-full">
              {busy ? 'Sending…' : sendFailed ? 'Try again' : 'Send'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
